import React, { useState, useRef, useEffect } from 'react';
import service from '../utils/request';
import { Switch } from 'antd';
import message from '../utils/message';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

const MailGroupModal = ({ isOpen, onClose, editData = null, allAliases }) => {
	const [emailAlias, setEmailAlias] = useState('');
	const [loading, setLoading] = useState(false);
	const [isPublic, setIsPublic] = useState(true);
	const [isPersonal, setIsPersonal] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedRows, setSelectedRows] = useState([]);
	const [selectAll, setSelectAll] = useState(false);
	const [users, setUsers] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [usersPerPage] = useState(10);
	const [emailError, setEmailError] = useState('');

	// Draggable functionality
	const [isDragging, setIsDragging] = useState(false);
	const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const modalRef = useRef(null);
	const headerRef = useRef(null);

	useEffect(() => {
		if (isOpen) {
			setPosition({ x: 0, y: 0 });

			if (!editData) {
				// Clear all form fields for new alias creation
				setEmailAlias('');
				setIsPublic(true);
				setIsPersonal(false);
				setSelectedRows([]);
				setSelectAll(false);
				setSearchTerm('');
				setCurrentPage(1);
				setEmailError('');
			}
		}
	}, [isOpen, editData]);

	useEffect(() => {
		if (editData && users.length > 0) {
			setEmailAlias(editData.email);
			const selectedIds = users.filter(user =>
				editData.forwardTo.includes(user.email)
			).map(user => user.id);
			setSelectedRows(selectedIds);

			if (editData.isPersonal) {
				setIsPersonal(true);
				setIsPublic(false);
			} else {
				setIsPersonal(false);
				setIsPublic(true);
			}
		} else if (!editData) {
			// Reset form when no edit data (creating new alias)
			setEmailAlias('');
			setIsPublic(true);
			setIsPersonal(false);
			setSelectedRows([]);
			setSelectAll(false);
			setEmailError('');
		}
	}, [editData, users]);

	const validateEmail = (email) => {
		const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
		return emailRegex.test(email);
	};

	const checkEmailDomain = (email) => {
		return !['igarment.net', 'netcloud.com.hk', 'padsystem.com'].includes(email.split('@')[1]);
	}

	const handleEmailChange = (e) => {
		const value = e.target.value;
		setEmailAlias(value);

		if (value && !validateEmail(value)) {
			setEmailError('Please enter a valid email address');
		} else if (value && checkEmailDomain(value)) {
			setEmailError('Please enter a valid email address with domain @igarment.net, @netcloud.com.hk or @padsystem.com');
		} else if (allAliases.find(alias => alias.email === value)) {
			setEmailError('Email alias already exists');
		} else {
			setEmailError('');
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!isPublic && !isPersonal) {
			message('error', 'Please select at least one type');
			return;
		}

		if (!emailAlias) {
			message('error', 'Please fill in the email alias');
			return;
		}

		if (emailError) {
			message('error', 'Please enter a valid email address');
			return;
		}

		if (selectedRows.length === 0) {
			message('error', 'Please select at least one forward address');
			return;
		}

		try {
			setLoading(true);

			const selectedUserEmails = users
				.filter(user => selectedRows.includes(user.id))
				.map(user => user.email);

			const data = {
				email_alias: emailAlias,
				is_personal: isPersonal ? 1 : 0,
				allow_public: isPublic ? 1 : 0,
				forward_addresses: selectedUserEmails
			};

			if (editData) {
				await service.post(`/mailgroup/create_special_alias/${editData.id}`, data);
				message('success', 'Email alias updated successfully');
			} else {
				await service.post('/mailgroup/create_special_alias', data);
				message('success', 'Email alias created successfully');
			}

			onClose();
		} catch (error) {
			message('error', error);
			console.error('Error saving email alias:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleSelectAll = () => {
		const newSelectAll = !selectAll;
		setSelectAll(newSelectAll);
		if (newSelectAll) {
			setSelectedRows(users.map(user => user.id));
		} else {
			setSelectedRows([]);
		}
	};

	const handlePublicChange = (checked) => {
		if (!checked && !isPersonal) {
			message('error', 'Please select at least one type');
		}
		setIsPublic(checked);
		if (checked) {
			setIsPersonal(false);
		}
	};

	const handlePersonalChange = (checked) => {
		if (!checked && !isPublic) {
			message('error', 'Please select at least one type');
		}
		setIsPersonal(checked);
		if (checked) {
			setIsPublic(false);
		}
	};

	const handleSelectRow = (id) => {
		const newSelectedRows = selectedRows.includes(id)
			? selectedRows.filter(rowId => rowId !== id)
			: [...selectedRows, id];
		setSelectedRows(newSelectedRows);
		setSelectAll(newSelectedRows.length === 6);
	};

	const renderSelectedUsers = () => {
		if (selectedRows.length === 0) {
			return null;
		}

		const selectedUsers = users.filter(user => selectedRows.includes(user.id));
		const headerHeight = 40;
		const rowHeight = 40;
		const padding = 16;
		const minRows = Math.min(selectedUsers.length, 4); // Show actual rows up to 4
		const dynamicHeight = headerHeight + (rowHeight * minRows) + padding;

		return (
			<div className="mb-4">
				<label className="block mb-2 text-sm font-medium text-left">
					Selected Forward Addresses ({selectedUsers.length})
				</label>
				<div
					className="border border-gray-200 rounded-md overflow-y-auto"
					style={{
						minHeight: `${dynamicHeight}px`,
						maxHeight: selectedUsers.length > 4 ? '200px' : `${dynamicHeight}px`
					}}
				>
					<div className="overflow-x-auto">
						<table className="w-full text-sm border-collapse">
							<thead>
								<tr className="bg-gray-50">
									<th className="w-8 p-2 border border-gray-200"></th>
									<th className="p-2 border border-gray-200 text-left">Login name</th>
									<th className="p-2 border border-gray-200 text-left">Email</th>
								</tr>
							</thead>
							<tbody>
								{sortUsers(selectedUsers, selectedRows).map(user => (
									<tr key={user.id}>
										<td className="p-2 text-center border border-gray-200">
											<input
												type="checkbox"
												checked={true}
												onChange={() => handleSelectRow(user.id)}
												className="w-4 h-4"
											/>
										</td>
										<td className="p-2 border text-left border-gray-200">{user.login_name}</td>
										<td className="p-2 border text-left border-gray-200">{user.email}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		);
	};

	const renderSearchTable = () => {
		if (!searchTerm.trim()) {
			return (
				<div className="p-4 text-center text-gray-500">
					Start typing to search for users...
				</div>
			);
		}

		return (
			<div className="overflow-x-auto">
				<table className="w-full text-sm border-collapse">
					<thead>
						<tr className="bg-gray-50">
							<th className="w-8 p-2 border border-gray-200"></th>
							<th className="p-2 border border-gray-200 text-left">Login name</th>
							<th className="p-2 border border-gray-200 text-left">Email</th>
						</tr>
					</thead>
					<tbody>
						{currentUsers.map(user => (
							<tr key={user.id}>
								<td className="p-2 text-center border border-gray-200">
									<input
										type="checkbox"
										checked={selectedRows.includes(user.id)}
										onChange={() => handleSelectRow(user.id)}
										className="w-4 h-4"
									/>
								</td>
								<td className="p-2 border border-gray-200 text-left">{user.login_name}</td>
								<td className="p-2 border border-gray-200 text-left">{user.email}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	};

	const renderSpecialAliasContent = () => {
		return (
			<form onSubmit={handleSubmit} className="space-y-4">
				<div>
					<label className="block mb-2 text-sm font-medium text-left">
						<span className="text-red-500">*</span> Email Alias
					</label>
					<input
						type="text"
						value={emailAlias}
						onChange={handleEmailChange}
						className={`w-full px-3 py-2 border ${emailError ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 ${emailError ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
						placeholder="Enter email alias"
						required
					/>
					{emailError && (
						<p className="mt-1 text-sm text-left text-red-500">{emailError}</p>
					)}
				</div>

				<div className="space-y-2">
					<div className="flex items-center gap-2">
						<span className="text-red-500">*</span>
						<span className="text-sm">Public</span>
						<Switch
							checked={isPublic}
							onChange={handlePublicChange}
							size="small"
							className="bg-gray-200"
							style={{ backgroundColor: isPublic ? '#1890ff' : '#d9d9d9' }}
						/>
					</div>
					<div className="flex items-center gap-2">
						<span className="text-red-500">*</span>
						<span className="text-sm">Personal</span>
						<Switch
							checked={isPersonal}
							onChange={handlePersonalChange}
							size="small"
							className="bg-gray-200"
							style={{ backgroundColor: isPersonal ? '#1890ff' : '#d9d9d9' }}
						/>
					</div>
				</div>
				{renderSelectedUsers()}
				<div>
					<label className="block mb-2 text-sm font-medium text-left">
						<span className="text-red-500">*</span> Add Forward Address
					</label>
					<div className="border border-gray-200 rounded-md">
						<div className="flex justify-end p-2">
							<div className="relative w-full sm:w-48">
								<input
									type="text"
									placeholder="Search..."
									value={searchTerm}
									onChange={(e) => {
										setSearchTerm(e.target.value);
										setCurrentPage(1);
									}}
									className="w-full px-3 py-1 pl-8 text-sm border border-gray-300 rounded-md"
								/>
								<span className="absolute left-2.5 top-1/2 transform -translate-y-1/2">
									<svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-400" fill="none"
										viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
											d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
									</svg>
								</span>
							</div>
						</div>
						{renderSearchTable()}
						{searchTerm.trim() && totalPages > 1 && (
							<div className="flex justify-center p-2 border-t border-gray-200">
								<div className="flex items-center gap-1">
									{Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
										<button
											key={page}
											onClick={(e) => handlePageChange(page, e)}
											className={`px-2 py-1 text-sm rounded ${page === currentPage ? 'bg-gray-200' : 'hover:bg-gray-100'
												}`}
										>
											{page}
										</button>
									))}
								</div>
							</div>
						)}
					</div>
				</div>

				<div className="flex justify-end gap-2 pt-4">
					<button
						type="button"
						onClick={onClose}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
					>
						Cancel
					</button>
					<button
						type="submit"
						className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
						disabled={loading}
					>
						{loading ? (
							<>
								<FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
								Loading...
							</>
						) : (
							'Save'
						)}
					</button>
				</div>
			</form>
		);
	};

	const fetchUsers = async () => {
		try {
			const response = await service.get('/mailgroup/users');
			setUsers(response || []);
		} catch (error) {
			message('error', error);
			console.error('Error fetching users:', error);
		}
	};

	useEffect(() => {
		if (isOpen) {
			fetchUsers();
		}
	}, [isOpen]);

	const sortUsers = (users, selectedRows) => {
		return [...users].sort((a, b) => {
			const aSelected = selectedRows.includes(a.id);
			const bSelected = selectedRows.includes(b.id);
			if (aSelected && !bSelected) return -1;
			if (!aSelected && bSelected) return 1;
			return a.login_name.toLowerCase().localeCompare(b.login_name.toLowerCase());
		});
	};

	const indexOfLastUser = currentPage * usersPerPage;
	const indexOfFirstUser = indexOfLastUser - usersPerPage;
	const currentUsers = (() => {
		if (searchTerm.trim()) {
			// Show search results when searching
			return sortUsers(
				users.filter(user =>
					user.login_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
					user.email.toLowerCase().includes(searchTerm.toLowerCase())
				),
				selectedRows
			).slice(indexOfFirstUser, indexOfLastUser);
		} else if (editData && selectedRows.length > 0) {
			// Show only selected users when editing (without search)
			return sortUsers(
				users.filter(user => selectedRows.includes(user.id)),
				selectedRows
			).slice(indexOfFirstUser, indexOfLastUser);
		}
		return [];
	})();

	const totalPages = (() => {
		if (searchTerm.trim()) {
			return Math.ceil(
				users.filter(user =>
					user.login_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
					user.email.toLowerCase().includes(searchTerm.toLowerCase())
				).length / usersPerPage
			);
		} else if (editData && selectedRows.length > 0) {
			return Math.ceil(selectedRows.length / usersPerPage);
		}
		return 0;
	})();

	// Handle page change
	const handlePageChange = (pageNumber, e) => {
		e.preventDefault(); // Prevent form submission
		setCurrentPage(pageNumber);
	};

	// Thêm useEffect để reset về trang 1 khi có sự thay đổi về selection
	useEffect(() => {
		setCurrentPage(1);
	}, [selectedRows]);

	// Draggable functionality
	const handleMouseDown = (e) => {
		// Check if the click is on the close button or its children
		if (e.target.closest('button')) {
			return; // Don't start dragging if clicking on any button
		}

		if (headerRef.current && headerRef.current.contains(e.target)) {
			setIsDragging(true);
			const rect = modalRef.current.getBoundingClientRect();
			setDragOffset({
				x: e.clientX - rect.left,
				y: e.clientY - rect.top
			});
			setPosition({
				x: rect.left,
				y: rect.top
			});
		}
	};

	const handleMouseMove = (e) => {
		if (!isDragging) return;

		const newX = e.clientX - dragOffset.x;
		const newY = e.clientY - dragOffset.y;

		const modalWidth = modalRef.current.offsetWidth;
		const modalHeight = modalRef.current.offsetHeight;
		const maxX = window.innerWidth - modalWidth;
		const maxY = window.innerHeight - modalHeight;

		setPosition({
			x: Math.max(0, Math.min(newX, maxX)),
			y: Math.max(0, Math.min(newY, maxY))
		});
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	useEffect(() => {
		if (isDragging) {
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
			return () => {
				document.removeEventListener('mousemove', handleMouseMove);
				document.removeEventListener('mouseup', handleMouseUp);
			};
		}
	}, [isDragging, dragOffset]);

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
			<div
				ref={modalRef}
				className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
				style={{
					position: isDragging || (position.x !== 0 || position.y !== 0) ? 'fixed' : 'relative',
					left: isDragging || (position.x !== 0 || position.y !== 0) ? `${position.x}px` : 'auto',
					top: isDragging || (position.x !== 0 || position.y !== 0) ? `${position.y}px` : 'auto',
					transform: isDragging || (position.x !== 0 || position.y !== 0) ? 'none' : 'none',
					cursor: isDragging ? 'grabbing' : 'default'
				}}
				onMouseDown={handleMouseDown}
			>
				{/* Header - Draggable area */}
				<div
					ref={headerRef}
					className="flex items-center justify-between p-6 border-b border-gray-200 cursor-grab active:cursor-grabbing"
				>
					<h2 className="text-xl font-semibold text-gray-800 select-none">
						{editData?.id ? 'Edit Mail Group' : 'Create Mail Group'}
					</h2>
					<button
						onClick={onClose}
						className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
						onMouseDown={(e) => e.stopPropagation()} // Prevent dragging when clicking close button
					>
						<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				{/* Modal content */}
				<div className="p-6">
					{renderSpecialAliasContent()}
				</div>
			</div>
		</div>
	);
};

export default MailGroupModal;
